import { useState, useEffect } from "react";
import { authenticatedFetch } from '../services/auth';

export const useStrategicMap = () => {
  const [strategicMapData, setStrategicMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState("api");

  useEffect(() => {
    let isMounted = true;

    const fetchStrategicMapData = async () => {
      try {
        if (!isMounted) return;

        setLoading(true);
        setError(null);

        console.log("Buscando dados do mapa estratégico...");

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          if (isMounted) {
            controller.abort();
          }
        }, 5000);

        try {
          // Substituindo o fetch padrão pelo fetch autenticado
          const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || ""}/api/bsc/map`, {
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!isMounted) return;
          clearTimeout(timeoutId);

          // Tratamento específico para erro 404
          if (response.status === 404) {
            console.log("⚠️ API retornou 404 - API offline");
            setError("API offline - Serviço temporariamente indisponível");
            setStrategicMapData([]);
            setDataSource("none");
            return;
          }

          if (!response.ok) {
            throw new Error(`Erro HTTP: ${response.status}`);
          }

          const data = await response.json();

          if (!isMounted) return;

          // Verificar se os dados são válidos - aceitar array vazio ou null/undefined
          if (data === null || data === undefined) {
            console.log("⚠️ API retornou dados nulos");
            setError(null); // Não definir como erro para permitir exibição do header
            setStrategicMapData([]);
            setDataSource("api");
            return;
          }

          if (!Array.isArray(data)) {
            console.warn("⚠️ API retornou formato não esperado:", typeof data);
            setError(null); // Não definir como erro para permitir exibição do header
            setStrategicMapData([]);
            setDataSource("api");
            return;
          }

          // Verificar se a API retornou dados vazios - não é um erro, é estado válido
          if (data.length === 0) {
            console.log("⚠️ API retornou dados vazios - sistema sem dados cadastrados");
            setError(null); // Não definir como erro para permitir exibição do header
            setStrategicMapData([]);
            setDataSource("api");
            return;
          }

          console.log("✅ Dados do mapa estratégico carregados da API");
          setStrategicMapData(data);
          setDataSource("api");
          setError(null);
        } catch (apiError) {
          if (!isMounted) return;

          clearTimeout(timeoutId);

          console.error("❌ Erro na API do mapa estratégico:", apiError);

          // Tratamento específico para diferentes tipos de erro
          if (apiError.name === "AbortError") {
            setError("Tempo limite de conexão excedido - API offline");
          } else if (apiError.message.includes("fetch")) {
            setError("API offline - Não foi possível conectar ao servidor");
          } else {
            setError(`Erro na API: ${apiError.message}`);
          }

          setStrategicMapData([]);
          setDataSource("none");
        }
      } catch (error) {
        if (!isMounted) return;

        console.error("❌ Erro crítico ao carregar mapa estratégico:", error);
        setError("Sistema temporariamente indisponível");
        setStrategicMapData([]);
        setDataSource("none");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Executar fetch
    fetchStrategicMapData().catch((error) => {
      if (isMounted) {
        console.error("Erro final capturado:", error);
        setError("Sistema temporariamente indisponível");
        setStrategicMapData([]);
        setDataSource("none");
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    strategicMapData,
    loading,
    error,
    dataSource,
  };
};